import { Lightbulb, Newspaper, Plus, Search, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { BlogTopicStatusBadge, FilterChips, Pagination } from '../components/ui/data';
import { ConfirmDialog, Drawer, Modal, useToast } from '../components/ui/overlays';
import {
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Table,
  Td,
  Th,
  Textarea,
  Tr,
} from '../components/ui/primitives';
import {
  useBulkDeleteTopics,
  useDeleteTopic,
  useImportTopics,
  useSaveTopic,
  useTopicStatusCounts,
  useTopics,
  useUpdateTopic,
  type TopicFilters,
} from '../hooks/queries';
import type { BlogTopic } from '../types/api';
import { cn, formatNumber, formatRelative } from '../lib/utils';
import { QueueContentModal } from './content/QueueContentModal';

const STATUS_OPTIONS = [
  'PENDING',
  'QUEUED',
  'DRAFTING',
  'DRAFTED',
  'PUBLISHED',
  'SKIPPED',
  'ARCHIVED',
] as const;

const STATUS_CHIPS = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'DRAFTING', label: 'Drafting' },
  { value: 'DRAFTED', label: 'Drafted' },
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const IMPORT_EXAMPLE = `How to price a peer-to-peer rental
The tax basics every host should know
Five ways to earn more from an idle asset`;

export const ContentTopicsPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const [filters, setFilters] = useState<TopicFilters>({
    page: 1,
    pageSize: 25,
    search: '',
    status: searchParams.get('status') ?? undefined,
    sortBy: 'createdAt',
    sortDir: 'desc',
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [queueOpen, setQueueOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState<BlogTopic | null>(null);

  const { data, isLoading, error, isFetching } = useTopics(projectId, filters);
  const { data: statusCounts } = useTopicStatusCounts(projectId);
  const bulkDelete = useBulkDeleteTopics(projectId);
  const saveTopic = useSaveTopic(projectId);
  const importTopics = useImportTopics(projectId);

  const topics = data?.items ?? [];
  const allSelected = topics.length > 0 && topics.every((topic) => selected.has(topic.id));

  const patch = (next: Partial<TopicFilters>) => {
    setFilters((current) => ({ ...current, page: 1, ...next }));
    setSelected(new Set());
    const params = new URLSearchParams(searchParams);
    if (next.status !== undefined) {
      next.status ? params.set('status', next.status) : params.delete('status');
    }
    setSearchParams(params, { replace: true });
  };

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Topics"
        description="Ideas the content agents will turn into blog posts, plus anything you add by hand. Select rows to queue content."
        actions={
          <>
            <Button icon={<Newspaper className="h-4 w-4" />} onClick={() => setQueueOpen(true)}>Create posts</Button>
            <Button icon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>
              Paste list
            </Button>
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => setAddOpen(true)}>
              Add topic
            </Button>
          </>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
          <div className="relative min-w-[14rem] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
            <Input
              value={filters.search ?? ''}
              onChange={(event) => patch({ search: event.target.value })}
              placeholder="Search title, angle, keywords, notes…"
              className="pl-8"
            />
          </div>

          <Select
            value={`${filters.sortBy}:${filters.sortDir}`}
            onChange={(event) => {
              const [sortBy, sortDir] = event.target.value.split(':');
              patch({ sortBy, sortDir: sortDir as 'asc' | 'desc' });
            }}
            className="w-auto"
          >
            <option value="createdAt:desc">Newest first</option>
            <option value="createdAt:asc">Oldest first</option>
            <option value="priority:desc">Highest priority</option>
            <option value="title:asc">Title A–Z</option>
          </Select>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
          <FilterChips
            options={STATUS_CHIPS as unknown as Array<{ value: string; label: string }>}
            value={filters.status}
            onChange={(status) => patch({ status })}
            counts={statusCounts}
          />
          {filters.status || filters.search ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<X className="h-3.5 w-3.5" />}
              onClick={() => patch({ status: undefined, search: '' })}
            >
              Clear
            </Button>
          ) : null}
        </div>

        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-line bg-brand/5 px-4 py-2.5">
            <span className="text-[13px] font-medium text-ink">{selected.size} selected</span>
            <Button
              variant="primary"
              size="sm"
              icon={<Newspaper className="h-3.5 w-3.5" />}
              onClick={() => setQueueOpen(true)}
            >
              Queue content
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </Button>
            <div className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        ) : null}

        {error ? (
          <div className="p-4">
            <ErrorBlock error={error} />
          </div>
        ) : topics.length === 0 ? (
          <EmptyState
            icon={<Lightbulb className="h-5 w-5" />}
            title="No topics match"
            description={
              filters.search || filters.status
                ? 'Try clearing the filters.'
                : 'Add a topic by hand or paste a list, then queue content so an agent drafts the posts.'
            }
            action={
              <Button variant="primary" onClick={() => setAddOpen(true)}>
                Add a topic
              </Button>
            }
          />
        ) : (
          <>
            <div className={cn('transition-opacity', isFetching && 'opacity-60')}>
              <Table>
                <thead>
                  <tr>
                    <Th className="w-9">
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        className="h-3.5 w-3.5 cursor-pointer accent-[rgb(var(--brand))]"
                        checked={allSelected}
                        onChange={() =>
                          setSelected(allSelected ? new Set() : new Set(topics.map((topic) => topic.id)))
                        }
                      />
                    </Th>
                    <Th>Topic</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Priority</Th>
                    <Th>Language</Th>
                    <Th className="text-right">Posts</Th>
                    <Th>Added</Th>
                  </tr>
                </thead>
                <tbody>
                  {topics.map((topic) => (
                    <Tr key={topic.id} className="cursor-pointer" onClick={() => setEditing(topic)}>
                      <Td onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${topic.title}`}
                          className="h-3.5 w-3.5 cursor-pointer accent-[rgb(var(--brand))]"
                          checked={selected.has(topic.id)}
                          onChange={() => toggle(topic.id)}
                        />
                      </Td>
                      <Td>
                        <div className="max-w-[28rem] truncate font-medium text-ink">{topic.title}</div>
                        {topic.angle ? (
                          <div className="max-w-[28rem] truncate text-xs text-faint">{topic.angle}</div>
                        ) : topic.keywords ? (
                          <div className="max-w-[28rem] truncate text-xs text-faint">{topic.keywords}</div>
                        ) : null}
                      </Td>
                      <Td>
                        <BlogTopicStatusBadge status={topic.status} />
                      </Td>
                      <Td className="text-right tabular-nums">
                        {topic.priority ?? <span className="text-faint">—</span>}
                      </Td>
                      <Td className="whitespace-nowrap text-muted">
                        {topic.language ?? <span className="text-faint">—</span>}
                      </Td>
                      <Td className="text-right tabular-nums text-muted">
                        {topic._count?.posts || <span className="text-faint">—</span>}
                      </Td>
                      <Td className="whitespace-nowrap text-faint">{formatRelative(topic.createdAt)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <Pagination
              page={data?.page ?? 1}
              totalPages={data?.totalPages ?? 1}
              total={data?.total ?? 0}
              pageSize={data?.pageSize ?? 25}
              onPage={(page) => setFilters((current) => ({ ...current, page }))}
            />
          </>
        )}
      </Card>

      <QueueContentModal
        open={queueOpen}
        onClose={() => setQueueOpen(false)}
        projectId={projectId}
        topicIds={selectedIds}
        onQueued={() => setSelected(new Set())}
      />

      <TopicDrawer topic={editing} projectId={projectId} onClose={() => setEditing(null)} />

      <AddTopicModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        saving={saveTopic.isPending}
        onSubmit={async (values) => {
          try {
            await saveTopic.mutateAsync(values);
            toast.success('Topic added');
            setAddOpen(false);
          } catch (caught) {
            toast.error(caught);
          }
        }}
      />

      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Paste a list of topics"
        description="One topic title per line. Duplicates are skipped automatically."
        footer={
          <Button variant="ghost" onClick={() => setImportOpen(false)}>
            Close
          </Button>
        }
      >
        <ImportForm
          busy={importTopics.isPending}
          onSubmit={async (values) => {
            try {
              const result = await importTopics.mutateAsync(values);
              toast.success(
                `Imported ${result.summary.created} new topic(s); ${result.summary.duplicates} duplicate(s) skipped.`,
              );
              setImportOpen(false);
            } catch (caught) {
              toast.error(caught);
            }
          }}
        />
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete topics"
        destructive
        confirmLabel={`Delete ${selected.size}`}
        loading={bulkDelete.isPending}
        message={`Permanently delete ${selected.size} topic(s)? Posts drafted from them are kept.`}
        onConfirm={async () => {
          try {
            const result = await bulkDelete.mutateAsync(selectedIds);
            toast.success(`Deleted ${result.deleted} topic(s)`);
            setSelected(new Set());
            setConfirmDelete(false);
          } catch (caught) {
            toast.error(caught);
          }
        }}
      />
    </Page>
  );
};

const AddTopicModal = ({
  open,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => void;
  saving: boolean;
}) => {
  const [form, setForm] = useState({
    title: '',
    angle: '',
    keywords: '',
    language: '',
    priority: '',
    tags: '',
    notes: '',
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a topic"
      description="A title is enough. The content agent uses the angle and keywords to shape the post."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={!form.title.trim()}
            onClick={() =>
              onSubmit({
                title: form.title.trim(),
                angle: form.angle || undefined,
                keywords: form.keywords || undefined,
                language: form.language || undefined,
                priority: form.priority === '' ? undefined : Number(form.priority),
                tags: form.tags || undefined,
                notes: form.notes || undefined,
              })
            }
          >
            Add topic
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" htmlFor="new-title" className="sm:col-span-2">
          <Input
            id="new-title"
            autoFocus
            value={form.title}
            onChange={(event) => setForm({ ...form, title: event.target.value })}
          />
        </Field>
        <Field label="Angle" htmlFor="new-angle" className="sm:col-span-2" hint="The take or promise of the post.">
          <Input
            id="new-angle"
            value={form.angle}
            onChange={(event) => setForm({ ...form, angle: event.target.value })}
          />
        </Field>
        <Field label="Keywords" htmlFor="new-keywords" hint="Comma separated.">
          <Input
            id="new-keywords"
            value={form.keywords}
            onChange={(event) => setForm({ ...form, keywords: event.target.value })}
          />
        </Field>
        <Field label="Language" htmlFor="new-language">
          <Input
            id="new-language"
            placeholder="en"
            value={form.language}
            onChange={(event) => setForm({ ...form, language: event.target.value })}
          />
        </Field>
        <Field label="Priority" htmlFor="new-priority" hint="Lower runs first.">
          <Input
            id="new-priority"
            type="number"
            value={form.priority}
            onChange={(event) => setForm({ ...form, priority: event.target.value })}
          />
        </Field>
        <Field
          label="Tags"
          htmlFor="new-tags"
          className="sm:col-span-2"
          hint="Comma separated. Added to Ghost on publish, alongside the channel's default tags."
        >
          <Input
            id="new-tags"
            value={form.tags}
            onChange={(event) => setForm({ ...form, tags: event.target.value })}
          />
        </Field>
        <Field label="Notes" htmlFor="new-notes" className="sm:col-span-2">
          <Textarea
            id="new-notes"
            rows={3}
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
          />
        </Field>
      </div>
    </Modal>
  );
};

const TopicDrawer = ({
  topic,
  projectId,
  onClose,
}: {
  topic: BlogTopic | null;
  projectId: string;
  onClose: () => void;
}) => {
  const toast = useToast();
  const updateTopic = useUpdateTopic(projectId);
  const deleteTopic = useDeleteTopic(projectId);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState({
    title: '',
    angle: '',
    keywords: '',
    tags: '',
    language: '',
    priority: '',
    status: 'PENDING',
    notes: '',
  });

  useEffect(() => {
    if (!topic) return;
    setDraft({
      title: topic.title,
      angle: topic.angle ?? '',
      keywords: topic.keywords ?? '',
      tags: topic.tags ?? '',
      language: topic.language ?? '',
      priority: topic.priority == null ? '' : String(topic.priority),
      status: topic.status,
      notes: topic.notes ?? '',
    });
  }, [topic]);

  const save = async () => {
    if (!topic) return;
    try {
      await updateTopic.mutateAsync({
        id: topic.id,
        title: draft.title.trim(),
        angle: draft.angle || undefined,
        keywords: draft.keywords || undefined,
        tags: draft.tags || undefined,
        language: draft.language || undefined,
        priority: draft.priority === '' ? undefined : Number(draft.priority),
        status: draft.status as BlogTopic['status'],
        notes: draft.notes || undefined,
      });
      toast.success('Topic saved');
      onClose();
    } catch (caught) {
      toast.error(caught);
    }
  };

  return (
    <>
      <Drawer
        open={Boolean(topic)}
        onClose={onClose}
        width="md"
        title={topic ? 'Edit topic' : 'Topic'}
        subtitle={topic ? <BlogTopicStatusBadge status={topic.status} /> : null}
        footer={
          topic ? (
            <>
              <Button
                variant="ghost"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </Button>
              <div className="flex-1" />
              <Button variant="primary" loading={updateTopic.isPending} disabled={!draft.title.trim()} onClick={save}>
                Save
              </Button>
            </>
          ) : null
        }
      >
        {topic ? (
          <div className="space-y-4 px-5 py-4">
            <Field label="Title" htmlFor="edit-title">
              <Input
                id="edit-title"
                value={draft.title}
                onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              />
            </Field>
            <Field label="Angle" htmlFor="edit-angle" hint="The take or promise of the post.">
              <Input
                id="edit-angle"
                value={draft.angle}
                onChange={(event) => setDraft({ ...draft, angle: event.target.value })}
              />
            </Field>
            <Field label="Keywords" htmlFor="edit-keywords" hint="Comma separated.">
              <Input
                id="edit-keywords"
                value={draft.keywords}
                onChange={(event) => setDraft({ ...draft, keywords: event.target.value })}
              />
            </Field>
            <Field
              label="Tags"
              htmlFor="edit-tags"
              hint="Comma separated. Sent to Ghost on publish, merged with the channel's default tags."
            >
              <Input
                id="edit-tags"
                value={draft.tags}
                onChange={(event) => setDraft({ ...draft, tags: event.target.value })}
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Language" htmlFor="edit-language">
                <Input
                  id="edit-language"
                  placeholder="en"
                  value={draft.language}
                  onChange={(event) => setDraft({ ...draft, language: event.target.value })}
                />
              </Field>
              <Field label="Priority" htmlFor="edit-priority" hint="Lower first.">
                <Input
                  id="edit-priority"
                  type="number"
                  value={draft.priority}
                  onChange={(event) => setDraft({ ...draft, priority: event.target.value })}
                />
              </Field>
              <Field label="Status" htmlFor="edit-status">
                <Select
                  id="edit-status"
                  value={draft.status}
                  onChange={(event) => setDraft({ ...draft, status: event.target.value })}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0) + status.slice(1).toLowerCase()}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <Field label="Notes" htmlFor="edit-notes">
              <Textarea
                id="edit-notes"
                rows={3}
                value={draft.notes}
                onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
              />
            </Field>
            {topic._count?.posts ? (
              <p className="text-xs text-faint">
                {topic._count.posts} post(s) drafted from this topic.
              </p>
            ) : null}
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete topic"
        destructive
        confirmLabel="Delete"
        loading={deleteTopic.isPending}
        message="Delete this topic? Posts already drafted from it are kept."
        onConfirm={async () => {
          if (!topic) return;
          try {
            await deleteTopic.mutateAsync(topic.id);
            toast.success('Topic deleted');
            setConfirmDelete(false);
            onClose();
          } catch (caught) {
            toast.error(caught);
          }
        }}
      />
    </>
  );
};

const ImportForm = ({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (values: { text: string; language?: string }) => void;
}) => {
  const [text, setText] = useState('');
  const [language, setLanguage] = useState('');

  const lineCount = text.trim() ? text.trim().split('\n').filter((line) => line.trim()).length : 0;

  return (
    <div className="space-y-4">
      <Field label="Language" htmlFor="import-language" hint="Applied to every topic in this list.">
        <Input
          id="import-language"
          placeholder="en"
          value={language}
          onChange={(event) => setLanguage(event.target.value)}
        />
      </Field>

      <Field label="Topics" htmlFor="import-text" hint={`One per line. Example:\n${IMPORT_EXAMPLE}`}>
        <Textarea
          id="import-text"
          rows={8}
          className="prompt-input"
          value={text}
          placeholder={IMPORT_EXAMPLE}
          onChange={(event) => setText(event.target.value)}
        />
      </Field>

      <Button
        variant="primary"
        className="w-full"
        loading={busy}
        disabled={!text.trim()}
        onClick={() => onSubmit({ text, language: language || undefined })}
      >
        Import {lineCount ? `${formatNumber(lineCount)} topic(s)` : ''}
      </Button>
    </div>
  );
};
